import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float } from '@react-three/drei';
import * as THREE from 'three';
import { TrackingData } from '../types';
import { HOLOLAB_COLORS } from '../constants';

interface HolographicIcosahedronProps {
  trackingData: React.MutableRefObject<TrackingData>;
}

// Holographic Icosahedron - Clean, scientific aesthetic
const HolographicIcosahedron: React.FC<HolographicIcosahedronProps> = ({ trackingData }) => {
  const icosahedronRef = useRef<THREE.Group>(null);
  const innerGlowRef = useRef<THREE.Mesh>(null);
  const pulseRef = useRef(0);
  
  // Create edge geometry for wireframe effect
  const { wireframeGeometry, solidGeometry, innerGeometry } = useMemo(() => {
    const icoGeo = new THREE.IcosahedronGeometry(1, 0);
    const edgesGeo = new THREE.EdgesGeometry(icoGeo);
    const innerGeo = new THREE.IcosahedronGeometry(0.6, 0);
    return {
      wireframeGeometry: edgesGeo,
      solidGeometry: icoGeo,
      innerGeometry: innerGeo
    };
  }, []);

  // Animation: rotation follows hand tracking, gentle pulse
  useFrame((state) => {
    if (icosahedronRef.current) {
      const data = trackingData.current;
      let targetRotationY = 0;
      let targetRotationX = 0;
      let isInteracting = false;

      // Determine rotation based on hand presence
      if (data.left.isDetected && data.right.isDetected) {
        // Average of both hands
        const avgX = (data.left.position.x + data.right.position.x) / 2;
        const avgY = (data.left.position.y + data.right.position.y) / 2;
        targetRotationY = (avgX - 0.5) * 2;
        targetRotationX = (avgY - 0.5) * 1;
        isInteracting = true;
      } else if (data.left.isDetected) {
        targetRotationY = (data.left.position.x - 0.5) * 2;
        targetRotationX = (data.left.position.y - 0.5) * 1;
        isInteracting = true;
      } else if (data.right.isDetected) {
        targetRotationY = (data.right.position.x - 0.5) * 2;
        targetRotationX = (data.right.position.y - 0.5) * 1;
        isInteracting = true;
      }

      // Base rotation (constant slow spin when idle)
      const baseRotation = state.clock.elapsedTime * 0.3;
      
      // Smooth interpolation
      if (isInteracting) {
        icosahedronRef.current.rotation.y += (targetRotationY - icosahedronRef.current.rotation.y) * 0.05;
        icosahedronRef.current.rotation.x += (targetRotationX - icosahedronRef.current.rotation.x) * 0.05;
        // Increase pulse when interacting
        pulseRef.current = Math.min(pulseRef.current + 0.02, 1);
      } else {
        // Idle rotation
        icosahedronRef.current.rotation.y = baseRotation;
        icosahedronRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.2) * 0.2;
        // Decrease pulse when not interacting
        pulseRef.current = Math.max(pulseRef.current - 0.01, 0);
      }
      
      // Additional Z-axis rotation for 3D effect
      icosahedronRef.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;

      // Inner glow pulse effect
      if (innerGlowRef.current) {
        const basePulse = 0.3 + Math.sin(state.clock.elapsedTime * 2) * 0.1;
        const interactionBoost = pulseRef.current * 0.3;
        const material = innerGlowRef.current.material as THREE.MeshBasicMaterial;
        material.opacity = basePulse + interactionBoost;
        
        // Subtle scale pulse
        const scalePulse = 0.6 + Math.sin(state.clock.elapsedTime * 1.5) * 0.05 + pulseRef.current * 0.1;
        innerGlowRef.current.scale.setScalar(scalePulse);
      }
    }
  });

  return (
    <Float speed={1.5} rotationIntensity={0.1} floatIntensity={0.3}>
      <group ref={icosahedronRef} scale={1.3}>
        {/* Primary Wireframe - Deep Blue */}
        <lineSegments geometry={wireframeGeometry}>
          <lineBasicMaterial
            color={HOLOLAB_COLORS.deepBlue}
            linewidth={2}
            transparent
            opacity={0.9}
          />
        </lineSegments>

        {/* Secondary Wireframe - Precision Blue (slightly larger) */}
        <lineSegments scale={1.05}>
          <edgesGeometry args={[new THREE.IcosahedronGeometry(1, 0)]} />
          <lineBasicMaterial
            color={HOLOLAB_COLORS.precisionBlue}
            linewidth={1}
            transparent
            opacity={0.4}
          />
        </lineSegments>

        {/* Semi-transparent fill for holographic depth */}
        <mesh geometry={solidGeometry}>
          <meshBasicMaterial
            color={HOLOLAB_COLORS.precisionBlue}
            transparent
            opacity={0.08}
            side={THREE.DoubleSide}
          />
        </mesh>

        {/* Inner glow core */}
        <mesh ref={innerGlowRef} geometry={innerGeometry}>
          <meshBasicMaterial
            color={HOLOLAB_COLORS.precisionBlue}
            transparent
            opacity={0.3}
          />
        </mesh>

        {/* Central point light for subtle glow */}
        <pointLight
          color={HOLOLAB_COLORS.precisionBlue}
          intensity={0.5}
          distance={3}
          decay={2}
        />
      </group>
    </Float>
  );
};

interface MascotAvatarProps {
  trackingData: React.MutableRefObject<TrackingData>;
}

const MascotAvatar: React.FC<MascotAvatarProps> = ({ trackingData }) => {
  return (
    <div className="w-full h-full">
      <Canvas camera={{ position: [0, 0, 4], fov: 45 }} gl={{ alpha: true }}>
        {/* Subtle ambient lighting */}
        <ambientLight intensity={0.4} />
        {/* Key light - soft white */}
        <pointLight position={[5, 5, 5]} intensity={0.6} color="#ffffff" />
        {/* Fill light - subtle blue tint */}
        <pointLight position={[-5, -3, 3]} intensity={0.3} color={HOLOLAB_COLORS.precisionBlue} />
        <HolographicIcosahedron trackingData={trackingData} />
      </Canvas>
    </div>
  );
};

export default MascotAvatar;
